import { useState, type FormEvent } from 'react';
import { Home, Lock } from 'lucide-react';
import type { IntegranteDto, ObservacionTitular } from '@lib/services/portal.service';
import { isValidEmail, limpiarTelefonoPegado, normalizarTelefonoArea, validarCelularTitular } from '@lib/validators';
import { capitalizeName } from '@lib/utils/text';
import { InfoAlert } from '@components/medicus-ui/InfoAlert';
import { ReportarCampo } from './ReportarCampo';
import { SugerenciaEmail } from './SugerenciaEmail';
import { CodigoPostalCombobox } from '@components/CodigoPostalCombobox';

interface Props {
  token: string;
  titular: IntegranteDto;
  observaciones: ObservacionTitular[];
  onSubmit: (patch: Partial<IntegranteDto>) => void;
  submitting: boolean;
}

type Editable = Partial<IntegranteDto>;

const SEXO_LABEL: Record<string, string> = {
  M: 'Masculino',
  F: 'Femenino',
};
const NACIONALIDAD_LABEL: Record<string, string> = {
  AR: 'Argentina',
  EX: 'Extranjera',
};
// Labels para mostrar el valor actual. Los únicos códigos que aceptamos del
// upload son S/C/P (Anexo II) y agrupamos varios estados civiles bajo cada
// código: C cubre Casado / Unión Civil / Convivencia; P cubre Separado /
// Divorciado / Viudo. D, V, N se mantienen para no romper observaciones
// históricas que ya quedaron persistidas con esos códigos.
const ESTADO_CIVIL_LABEL: Record<string, string> = {
  S: 'Soltero',
  C: 'Casado / Unión Civil / Convivencia',
  P: 'Separado / Divorciado / Viudo',
  D: 'Divorciado',
  V: 'Viudo',
  N: 'Concubinato',
};
const TIPO_DOC_LABEL: Record<string, string> = {
  DNI: 'DNI',
  PA: 'Pasaporte',
};

// Opciones que mostramos en los <select> del popover "Reportar como
// incorrecto". Limitamos `estadoCivil` a S/C/P (Anexo II) — son los
// únicos códigos que el sistema acepta en el upload del padrón.
const OPC_SEXO = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
];
const OPC_NACIONALIDAD = [
  { value: 'AR', label: 'Argentina' },
  { value: 'EX', label: 'Extranjera' },
];
const OPC_ESTADO_CIVIL = [
  { value: 'S', label: 'Soltero' },
  { value: 'C', label: 'Casado / Unión Civil / Convivencia' },
  { value: 'P', label: 'Separado / Divorciado / Viudo' },
];
const OPC_TIPO_DOC = [
  { value: 'DNI', label: 'DNI' },
  { value: 'PA', label: 'Pasaporte' },
];

export function TitularStep({ token, titular, observaciones, onSubmit, submitting }: Props) {
  // Solo persistimos campos de contacto y dirección. Lo demás es read-only.
  const [v, setV] = useState<Editable>({
    domicilio: titular.domicilio ?? '',
    numero: titular.numero ?? '',
    piso: titular.piso ?? '',
    depto: titular.depto ?? '',
    cp: titular.cp ?? '',
    areaCelular: titular.areaCelular ?? '',
    numeroCelular: titular.numeroCelular ?? '',
    email: titular.email ?? '',
  });
  const [err, setErr] = useState<Record<string, string>>({});

  function set<K extends keyof Editable>(key: K, val: Editable[K]) {
    setV((prev) => ({ ...prev, [key]: val }));
    setErr((prev) => ({ ...prev, [key]: '' }));
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.domicilio?.trim()) e.domicilio = 'Ingresá tu domicilio';
    if (!v.cp || !/^\d{4}$/.test(v.cp) || /^0+$/.test(v.cp)) e.cp = 'CP de 4 dígitos (no puede ser 0)';

    // Email OBLIGATORIO — el titular puede cambiarlo pero no dejarlo vacío.
    const emailTrim = (v.email ?? '').trim();
    if (!emailTrim) e.email = 'El email es obligatorio';
    else if (!isValidEmail(emailTrim)) e.email = 'Email inválido';

    // Celular OBLIGATORIO con área + número que sumen 10 dígitos.
    const valCel = validarCelularTitular(v.areaCelular ?? '', v.numeroCelular ?? '');
    if (!valCel.ok) {
      if (valCel.errorArea) e.areaCelular = valCel.errorArea;
      if (valCel.errorNumero) e.numeroCelular = valCel.errorNumero;
      if (valCel.errorGenerico) {
        // Si es un error de suma (faltan/sobran dígitos), lo mostramos sobre el campo número.
        if (!e.areaCelular && !e.numeroCelular) e.numeroCelular = valCel.errorGenerico;
      }
    }

    setErr(e);
    return Object.keys(e).length === 0;
  }

  /**
   * Cuando el usuario pega un teléfono completo en el campo Celular (ej.
   * "+54 9 11 4234-5678") lo parseamos y autocompletamos área + número.
   * Si solo tipea normalmente, conservamos comportamiento (solo dígitos).
   */
  function handlePegarNumero(raw: string): void {
    // Si parece pegado (espacios, +, paréntesis o más de 8 dígitos), intentar parser completo.
    const looksPasted = /[+\s()-]/.test(raw) || raw.replace(/\D/g, '').length > 8;
    if (looksPasted) {
      const cleaned = limpiarTelefonoPegado(raw);
      if (cleaned) {
        // Hint de split: si trae "11" inicial es CABA (área de 2 dig), sino probamos 3 dig.
        const splitArea = cleaned.startsWith('11') ? 2 : 3;
        const area = cleaned.slice(0, splitArea);
        const numero = cleaned.slice(splitArea);
        setV((prev) => ({ ...prev, areaCelular: area, numeroCelular: numero }));
        setErr((prev) => ({ ...prev, areaCelular: '', numeroCelular: '' }));
        return;
      }
    }
    set('numeroCelular', raw.replace(/\D/g, '').slice(0, 8));
  }

  /**
   * Cuando el usuario sale del campo número, intentamos auto-corregir
   * casos donde área + número no suman 10 dígitos limpios (ej. el usuario
   * tipeó todo el número en el campo Celular).
   */
  function handleBlurCelular(): void {
    const norm = normalizarTelefonoArea(v.areaCelular ?? '', v.numeroCelular ?? '');
    if (norm.area !== v.areaCelular || norm.numero !== v.numeroCelular) {
      setV((prev) => ({ ...prev, areaCelular: norm.area, numeroCelular: norm.numero }));
    }
  }

  function handle(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(v);
  }

  const yaReportado = (campo: string) =>
    observaciones.some((o) => o.asociadoId === titular.id && o.campo === campo);

  return (
    <form onSubmit={handle} className="onboarding__form" noValidate>
      <aside
        style={{
          padding: '10px 12px',
          marginBottom: 16,
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          color: '#1E40AF',
          borderRadius: 10,
          fontSize: 13,
        }}
      >
        Algunos de tus datos ya están cargados. Si tenés que corregirlos, usá el botón{' '}
        <strong>Reportar como incorrecto</strong>. Solo podrás editar los campos de
        Contacto y Dirección.
      </aside>

      <Section title="Información personal (no editable)">
        <Locked
          label="Tipo doc"
          value={TIPO_DOC_LABEL[titular.tipoDoc ?? ''] ?? titular.tipoDoc ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="tipoDoc"
              labelCampo="Tipo doc"
              valorActual={titular.tipoDoc}
              yaReportado={yaReportado('tipoDoc')}
              tipo="enum"
              opciones={OPC_TIPO_DOC}
            />
          }
        />
        <Locked
          label="DNI"
          value={titular.documento ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="documento"
              labelCampo="DNI"
              valorActual={titular.documento}
              yaReportado={yaReportado('documento')}
              tipo="text"
            />
          }
        />
        <Locked
          label="Fecha de nacimiento"
          value={titular.fechaNacimiento ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="fechaNacimiento"
              labelCampo="Fecha de nacimiento"
              valorActual={titular.fechaNacimiento}
              yaReportado={yaReportado('fechaNacimiento')}
              tipo="date"
            />
          }
        />
        <Locked
          label="CUIL"
          value={titular.cuil ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="cuil"
              labelCampo="CUIL"
              valorActual={titular.cuil}
              yaReportado={yaReportado('cuil')}
              tipo="numeric"
            />
          }
        />
        <Locked
          label="Apellido"
          value={capitalizeName(titular.apellSolt) || '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="apellSolt"
              labelCampo="Apellido"
              valorActual={titular.apellSolt}
              yaReportado={yaReportado('apellSolt')}
              tipo="text"
            />
          }
        />
        <Locked
          label="Nombres"
          value={capitalizeName(titular.nombres) || '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="nombres"
              labelCampo="Nombres"
              valorActual={titular.nombres}
              yaReportado={yaReportado('nombres')}
              tipo="text"
            />
          }
        />
        <Locked
          label="Sexo"
          value={SEXO_LABEL[titular.sexo ?? ''] ?? titular.sexo ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="sexo"
              labelCampo="Sexo"
              valorActual={titular.sexo}
              yaReportado={yaReportado('sexo')}
              tipo="enum"
              opciones={OPC_SEXO}
              permiteComentario
            />
          }
        />
        <Locked
          label="Estado civil"
          value={ESTADO_CIVIL_LABEL[titular.estadoCivil ?? ''] ?? titular.estadoCivil ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="estadoCivil"
              labelCampo="Estado civil"
              valorActual={titular.estadoCivil}
              yaReportado={yaReportado('estadoCivil')}
              tipo="enum"
              opciones={OPC_ESTADO_CIVIL}
            />
          }
        />
        <Locked
          label="Nacionalidad"
          value={NACIONALIDAD_LABEL[titular.nacionalidad ?? ''] ?? titular.nacionalidad ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={titular.id}
              campo="nacionalidad"
              labelCampo="Nacionalidad"
              valorActual={titular.nacionalidad}
              yaReportado={yaReportado('nacionalidad')}
              tipo="enum"
              opciones={OPC_NACIONALIDAD}
            />
          }
        />
      </Section>

      <Section title="Domicilio">
        <div style={{ marginBottom: 12 }}>
          <InfoAlert icon={Home}>
            Cargá el domicilio actual en el que vivís (no el que figura en el
            DNI ni el de tu lugar de trabajo).
          </InfoAlert>
        </div>
        <Field
          label="Calle"
          value={v.domicilio ?? ''}
          onChange={(x) => set('domicilio', x)}
          autoComplete="address-line1"
          error={err.domicilio}
        />
        <div className="onboarding__rowGrid onboarding__rowGrid--pair">
          <Field
            label="Número"
            value={v.numero ?? ''}
            onChange={(x) => set('numero', x.replace(/\D/g, ''))}
            inputMode="numeric"
            autoComplete="address-line2"
          />
          <Field
            label="Piso"
            value={v.piso ?? ''}
            onChange={(x) => set('piso', x)}
            inputMode="text"
          />
        </div>
        <div className="onboarding__rowGrid onboarding__rowGrid--pair">
          <Field
            label="Depto"
            value={v.depto ?? ''}
            onChange={(x) => set('depto', x)}
            inputMode="text"
          />
          <CodigoPostalCombobox
            label="CP *"
            value={v.cp ?? ''}
            onChange={(cp) => set('cp', cp)}
            error={err.cp}
            hint="Escribí el código o el nombre de la localidad"
          />
        </div>
      </Section>

      <Section title="Contacto">
        <div className="onboarding__rowGrid onboarding__rowGrid--pair">
          <Field
            label="Característica"
            value={v.areaCelular ?? ''}
            onChange={(x) => set('areaCelular', x.replace(/\D/g, '').slice(0, 4))}
            onBlur={handleBlurCelular}
            inputMode="numeric"
            maxLength={4}
            hint="Sin 0 ni 15. Ej: 11"
            error={err.areaCelular}
          />
          <Field
            label="Celular"
            value={v.numeroCelular ?? ''}
            onChange={handlePegarNumero}
            onBlur={handleBlurCelular}
            inputMode="numeric"
            autoComplete="tel-national"
            type="tel"
            hint="Opcional. Si lo cargás, área + número deben sumar 10 dígitos. Podés pegar el teléfono completo y se separa solo."
            error={err.numeroCelular}
            wide
          />
        </div>
        <Field
          label="Email *"
          value={v.email ?? ''}
          onChange={(x) => set('email', x.trim().toLowerCase())}
          hint="En este correo recibirás novedades e información importante."
          error={err.email}
          type="email"
          inputMode="email"
          autoComplete="email"
        />
        <SugerenciaEmail email={v.email ?? ''} onUsar={(corregido) => set('email', corregido)} />
      </Section>

      <div className="onboarding__actions">
        <button className="btn btn-primary" type="submit" disabled={submitting}>
          <span className="btn-font-style">
            {submitting ? 'Guardando…' : 'Continuar'}
          </span>
        </button>
      </div>
    </form>
  );
}

// ---------- helpers de form ----------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="onboarding__section">
      <legend className="onboarding__sectionTitle">{title}</legend>
      <div className="onboarding__sectionBody">{children}</div>
    </fieldset>
  );
}

function Locked({
  label,
  value,
  reportar,
}: {
  label: string;
  value: string;
  reportar?: React.ReactNode;
}) {
  return (
    <div
      className="onboarding__field onboarding__field--readonly"
      style={{ background: '#F9FAFB' }}
    >
      <span
        className="onboarding__fieldLabel"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <Lock size={11} style={{ opacity: 0.5 }} /> {label}
      </span>
      <span className="onboarding__fieldValue" style={{ fontWeight: 500 }}>
        {value}
      </span>
      <div style={{ marginTop: 4 }}>{reportar}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  onBlur,
  hint,
  error,
  type,
  inputMode,
  autoComplete,
  maxLength,
  wide,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  onBlur?: () => void;
  hint?: string;
  error?: string;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'url' | 'search' | 'decimal' | 'none';
  autoComplete?: string;
  maxLength?: number;
  wide?: boolean;
}) {
  return (
    <div className={`onboarding__field ${wide ? 'onboarding__field--wide' : ''}`}>
      <label className="onboarding__fieldLabel">{label}</label>
      <input
        className="input"
        type={type ?? 'text'}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        disabled={!onChange}
      />
      {hint && !error && <span className="onboarding__fieldHint">{hint}</span>}
      {error && <span className="onboarding__fieldError">{error}</span>}
    </div>
  );
}
